import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api';
import { Spinner } from '@/components/Spinner';

/**
 * Lists the documents attached to a jacket (below the uploader on the Upload
 * Images page). Mirrors the live app's attached-files list, with a remove action.
 */
export function JacketAttachments({ jacketId }: { jacketId: number }) {
  const queryClient = useQueryClient();
  const { data: files = [], isLoading } = useQuery({
    queryKey: ['jacket-attachments', jacketId],
    queryFn: () => api.getJacketAttachments(jacketId),
  });

  const removeMutation = useMutation({
    mutationFn: (docId: number) => api.removeJacketDoc(jacketId, docId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jacket-attachments', jacketId] }),
  });

  const onRemove = (docId: number, filename: string) => {
    if (window.confirm(`Remove “${filename}”? This cannot be undone.`)) {
      removeMutation.mutate(docId);
    }
  };

  return (
    <section className="ast-card ast-form-section">
      <h3 className="ast-section-title">Attached Files</h3>
      {isLoading ? (
        <div className="py-3 text-center"><Spinner /></div>
      ) : files.length === 0 ? (
        <p className="text-muted small mb-0">No files attached yet.</p>
      ) : (
        <ul className="ast-attachment-list">
          {files.map((f) => (
            <li key={f.id} className="ast-attachment-row">
              <i className="bi bi-file-earmark-pdf" />
              <span className="ast-attachment-name">{f.filename}</span>
              <span className="ms-auto d-flex align-items-center gap-2">
                <a className="ast-link" href={f.url} target="_blank" rel="noopener noreferrer">View</a>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  title="Remove"
                  disabled={removeMutation.isPending}
                  onClick={() => onRemove(f.id, f.filename)}
                >
                  <i className="bi bi-trash" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
